import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, Check, Wand2, Loader2, Sparkles, Info, BookOpen, Youtube, RefreshCw, Pencil, Wand, Eraser, MapPin, Trophy, Users, PartyPopper, Printer, FileDown, Eye, Ear, Hand } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import WorksheetPanel from "@/components/WorksheetPanel";

const SUBJECTS = ["Math", "Reading", "Writing", "Science", "Social Studies", "Art", "Music", "PE", "Life Skills"];

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function parseYmd(iso) {
  // Treat "YYYY-MM-DD" as a local date (avoid UTC drift shifting the day).
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  return new Date(y, m - 1, d);
}

function weekDates(anchor) {
  const start = new Date(anchor);
  start.setDate(start.getDate() - start.getDay()); // Sunday
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

const emptyLesson = { student_id: "", title: "", subject: "Math", date: ymd(new Date()), time: "09:00", duration_minutes: 60, notes: "", status: "planned", entry_type: "lesson" };

// Non-academic entry types teachers can add to the calendar (field trips,
// sports, family days, etc.) — no student/subject required.
const ENTRY_TYPES = [
  { id: "lesson",     label: "Lesson",     icon: BookOpen,    iconClass: "text-amber-600",   textClass: "text-amber-700",   ring: "border-amber-200",   bg: "bg-white" },
  { id: "field_trip", label: "Field trip", icon: MapPin,      iconClass: "text-emerald-600", textClass: "text-emerald-700", ring: "border-emerald-200", bg: "bg-emerald-50" },
  { id: "sports",     label: "Sports",     icon: Trophy,      iconClass: "text-blue-600",    textClass: "text-blue-700",    ring: "border-blue-200",    bg: "bg-blue-50" },
  { id: "family_day", label: "Family day", icon: Users,       iconClass: "text-pink-600",    textClass: "text-pink-700",    ring: "border-pink-200",    bg: "bg-pink-50" },
  { id: "holiday",    label: "Holiday",    icon: PartyPopper, iconClass: "text-violet-600",  textClass: "text-violet-700",  ring: "border-violet-200",  bg: "bg-violet-50" },
];
const entryMeta = (id) => ENTRY_TYPES.find((t) => t.id === id) || ENTRY_TYPES[0];

export default function Schedule() {
  const [students, setStudents] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [anchor, setAnchor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyLesson);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clearBusy, setClearBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(null); // lesson.id currently regenerating
  const [printBusy, setPrintBusy] = useState(false);
  const [packetBusy, setPacketBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const [studentFilter, setStudentFilter] = useState("all");

  // Color palette for multi-child mode
  const STUDENT_COLORS = ["#F59E0B", "#10B981", "#3B82F6", "#EC4899", "#8B5CF6", "#EF4444", "#14B8A6"];
  const studentColor = (sid) => {
    const idx = students.findIndex((s) => s.id === sid);
    return STUDENT_COLORS[Math.max(0, idx) % STUDENT_COLORS.length];
  };

  // Auto-build state
  const [autoOpen, setAutoOpen] = useState(false);
  const [autoForm, setAutoForm] = useState({
    student_id: "",
    start_date: ymd(new Date()),
    days: 5,
    lessons_per_day: 4,
    duration_minutes: 45,
    use_ai: true,
    replace_existing: false,
  });
  const [autoBusy, setAutoBusy] = useState(false);

  // Lesson-help modal state
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpLesson, setHelpLesson] = useState(null);
  const [helpContent, setHelpContent] = useState("");
  const [helpBusy, setHelpBusy] = useState(false);

  // Textbook + YouTube recommendations (per lesson, cached server-side)
  const [resources, setResources] = useState(null);
  const [resourcesBusy, setResourcesBusy] = useState(false);

  // Learning-style override for the teaching help (visual | auditory | kinesthetic | "" = student default)
  const [helpStyle, setHelpStyle] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);

  const loadResources = async (lessonId, refresh = false) => {
    setResourcesBusy(true);
    try {
      const { data } = await api.post(`/lessons/${lessonId}/resources`, null, {
        params: refresh ? { refresh: true } : {},
      });
      setResources(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setResourcesBusy(false);
    }
  };

  const openHelp = async (lesson) => {
    setHelpLesson(lesson);
    setHelpContent("");
    setResources(null);
    setHelpStyle("");
    setHelpOpen(true);
    setHelpBusy(true);
    try {
      const { data } = await api.post(`/lessons/${lesson.id}/help`, {});
      setHelpContent(data.content || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setHelpBusy(false);
    }
    // If the lesson already has cached recs, hydrate them without spending a call.
    if (lesson.resource_recs) {
      setResources(lesson.resource_recs);
    }
  };

  // Regenerate help with a new (or unset) learning-style override.
  const regenerateHelpWithStyle = async (styleValue) => {
    if (!helpLesson) return;
    setHelpStyle(styleValue);
    setHelpContent("");
    setHelpBusy(true);
    try {
      const body = styleValue ? { learning_style: styleValue } : {};
      const { data } = await api.post(`/lessons/${helpLesson.id}/help`, body);
      setHelpContent(data.content || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setHelpBusy(false);
    }
  };

  // Export the current lesson plan as a PDF download.
  const exportHelpPdf = async () => {
    if (!helpLesson) return;
    setPdfBusy(true);
    try {
      const params = helpStyle ? { learning_style: helpStyle } : {};
      const res = await api.get(`/lessons/${helpLesson.id}/help/pdf`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = (helpLesson.title || "lesson").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      a.download = `${slug}-lesson-plan.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't build PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const days = useMemo(() => weekDates(anchor), [anchor]);

  const load = async () => {
    const s = await api.get("/students");
    setStudents(s.data);
    const l = await api.get("/lessons");
    setLessons(l.data);
    if (s.data.length && !form.student_id) setForm((f) => ({ ...f, student_id: s.data[0].id }));
    if (s.data.length && !autoForm.student_id) setAutoForm((f) => ({ ...f, student_id: s.data[0].id }));
  };

  useEffect(() => { load().catch(() => {}); }, []);

  // Deep-link support: ?student=<id>&open=auto
  useEffect(() => {
    const sid = params.get("student");
    if (sid) setAutoForm((f) => ({ ...f, student_id: sid }));
    if (params.get("open") === "auto") {
      setAutoOpen(true);
      // Consume the params so re-renders don't reopen
      const p = new URLSearchParams(params);
      p.delete("open");
      setParams(p, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  const runAutoBuild = async () => {
    if (!autoForm.student_id) {
      toast.error("Pick a student first");
      return;
    }
    setAutoBusy(true);
    try {
      const { data } = await api.post("/schedule/auto-build", autoForm);
      toast.success(`Auto-built ${data.created_count} lessons across ${data.days.length} days`);
      setAutoOpen(false);
      // Jump the calendar to the first auto-built day
      if (data.days.length) setAnchor(parseYmd(data.days[0]));
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setAutoBusy(false);
    }
  };

  const openAdd = (dateStr) => {
    setEditingId(null);
    setForm({ ...emptyLesson, date: dateStr, student_id: students[0]?.id || "" });
    setOpen(true);
  };

  const openEdit = (lesson) => {
    setEditingId(lesson.id);
    setForm({
      student_id: lesson.student_id || "",
      title: lesson.title || "",
      subject: lesson.subject || "Math",
      date: lesson.date,
      time: lesson.time || "09:00",
      duration_minutes: lesson.duration_minutes || 60,
      notes: lesson.notes || "",
      status: lesson.status || "planned",
      entry_type: lesson.entry_type || "lesson",
    });
    setOpen(true);
  };

  const save = async () => {
    const isLesson = (form.entry_type || "lesson") === "lesson";
    if (isLesson && !form.student_id) {
      toast.error("Pick a student for this lesson");
      return;
    }
    if (!form.title) {
      toast.error("Give this entry a title");
      return;
    }
    setSaving(true);
    try {
      // For non-lesson entries, blank out subject so the backend stores null.
      const payload = { ...form, subject: isLesson ? form.subject : null, student_id: form.student_id || null };
      if (editingId) {
        await api.patch(`/lessons/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/lessons", payload);
        toast.success(isLesson ? "Lesson added" : `${entryMeta(form.entry_type).label} added`);
      }
      setOpen(false);
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const regenerateLesson = async (l) => {
    setRegenBusy(l.id);
    try {
      const { data } = await api.post(`/lessons/${l.id}/regenerate`);
      toast.success(`New title: "${data.title}"`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRegenBusy(null);
    }
  };

  const printFamilyCalendar = async () => {
    // Pick the month of whichever day is currently focused (visible week's Monday)
    const focus = days[0] || new Date();
    const monthStr = `${focus.getFullYear()}-${String(focus.getMonth() + 1).padStart(2, "0")}`;
    setPrintBusy(true);
    try {
      const params = { month: monthStr };
      if (studentFilter !== "all") params.student_id = studentFilter;
      const { data } = await api.get("/portfolio/family-calendar", {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank");
      // Release after the browser has had time to open the tab
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not build calendar PDF");
    } finally {
      setPrintBusy(false);
    }
  };

  const buildWeeklyPacket = async () => {
    if (studentFilter === "all") {
      toast.error("Pick a single student first — the weekly packet is per-student.");
      return;
    }
    const weekStartIso = ymd(days[0]);
    const student = studentFor(studentFilter);
    setPacketBusy(true);
    const toastId = toast.loading(
      `Building ${student?.name || "student"}'s week-of-${weekStartIso} packet… lesson plans may take ~30–60s.`,
    );
    try {
      const res = await api.get("/lessons/packet/weekly", {
        params: { student_id: studentFilter, week_start: weekStartIso },
        responseType: "blob",
        timeout: 180000,  // Plan generation can take 30-60s; give it plenty of headroom
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = (student?.name || "student").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      a.download = `${slug}-week-of-${weekStartIso}-lesson-packet.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Weekly packet ready!", { id: toastId });
    } catch (e) {
      // With responseType:'blob', e.response.data is a Blob — read as text to extract the JSON error detail
      let detail = null;
      try {
        const blob = e.response?.data;
        if (blob && typeof blob.text === "function") {
          const txt = await blob.text();
          const json = JSON.parse(txt);
          detail = formatApiErrorDetail(json.detail);
        }
      } catch { /* ignore parse failure */ }
      if (e.response?.status === 404) {
        toast.error(detail || "No lessons found in this week for that student.", { id: toastId });
      } else {
        toast.error(detail || "Couldn't build the weekly packet", { id: toastId });
      }
    } finally {
      setPacketBusy(false);
    }
  };

  const clearVisibleWeek = async () => {
    const startIso = ymd(days[0]);
    const endIso = ymd(days[days.length - 1]);
    const scopeLabel = studentFilter === "all" ? "all students" : (studentName(studentFilter));
    if (!window.confirm(`Clear all planned lessons for ${scopeLabel} between ${startIso} and ${endIso}?\n\nField trips, sports, family days, and holidays will be preserved.`)) return;
    setClearBusy(true);
    try {
      const body = {
        start_date: startIso,
        end_date: endIso,
        include_completed: false,
        include_events: false,
      };
      if (studentFilter !== "all") body.student_id = studentFilter;
      const { data } = await api.post("/schedule/clear", body);
      toast.success(`Cleared ${data.deleted_count} lesson(s). Events preserved.`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setClearBusy(false);
    }
  };

  const restoreLesson = async (snapshot) => {
    try {
      // Rebuild the create payload — strip server-managed keys, keep everything else.
      const payload = {
        student_id: snapshot.student_id || null,
        title: snapshot.title,
        subject: snapshot.subject || null,
        date: snapshot.date,
        time: snapshot.time || "09:00",
        duration_minutes: snapshot.duration_minutes || 60,
        notes: snapshot.notes || "",
        status: snapshot.status || "planned",
        entry_type: snapshot.entry_type || "lesson",
      };
      await api.post("/lessons", payload);
      toast.success(`Restored "${snapshot.title}"`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not restore");
    }
  };

  const remove = async (l) => {
    const em = entryMeta(l.entry_type || "lesson");
    if (!window.confirm(`Delete "${l.title}"?\n\nThis ${em.label.toLowerCase()} will be removed permanently.`)) return;
    // Snapshot BEFORE deleting so an Undo click can rebuild it.
    const snapshot = { ...l };
    try {
      await api.delete(`/lessons/${l.id}`);
      await load();
      toast.success(`${em.label} deleted`, {
        duration: 10000,
        action: {
          label: "Undo",
          onClick: () => restoreLesson(snapshot),
        },
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const toggleDone = async (l) => {
    try {
      await api.patch(`/lessons/${l.id}`, { status: l.status === "completed" ? "planned" : "completed" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const studentName = (id) => students.find((s) => s.id === id)?.name || "Student";
  const studentFor = (id) => students.find((s) => s.id === id) || null;
  const isPremium = (id) => {
    const s = studentFor(id);
    if (!s || s.subscription_tier !== "premium") return false;
    return ["active", "trial", "trialing"].includes(s.subscription_status);
  };

  const lessonsByDate = useMemo(() => {
    const map = {};
    const filtered = studentFilter === "all" ? lessons : lessons.filter((l) => l.student_id === studentFilter);
    for (const l of filtered) {
      (map[l.date] = map[l.date] || []).push(l);
    }
    return map;
  }, [lessons, studentFilter]);

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber-600">Planner</div>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">Schedule</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={studentFilter} onValueChange={setStudentFilter}>
            <SelectTrigger className="rounded-full h-9 w-48" data-testid="student-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All students</SelectItem>
              {students.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" className="rounded-full" onClick={() => { const d = new Date(anchor); d.setDate(d.getDate() - 7); setAnchor(d); }} data-testid="week-prev">
            ← Previous
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => setAnchor(new Date())} data-testid="week-today">
            Today
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => { const d = new Date(anchor); d.setDate(d.getDate() + 7); setAnchor(d); }} data-testid="week-next">
            Next →
          </Button>
          <Button
            onClick={() => setAutoOpen(true)}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="auto-build-btn"
          >
            <Wand2 className="w-4 h-4 mr-2" /> Auto-build week
          </Button>
          <Button
            variant="outline"
            onClick={printFamilyCalendar}
            disabled={printBusy}
            className="rounded-full border-2 border-stone-200 hover:border-amber-300 hover:text-amber-600"
            data-testid="print-calendar-btn"
            title="Download this month's calendar as a printable PDF (mixes lessons + events)"
          >
            <Printer className="w-4 h-4 mr-2" /> {printBusy ? "Building…" : "Print month"}
          </Button>
          <Button
            variant="outline"
            onClick={buildWeeklyPacket}
            disabled={packetBusy || studentFilter === "all"}
            className="rounded-full border-2 border-stone-200 hover:border-amber-300 hover:text-amber-600 disabled:opacity-60"
            data-testid="weekly-packet-btn"
            title={studentFilter === "all"
              ? "Pick one student first — the weekly packet is per-student"
              : "Bundle every lesson for the visible week into one printable PDF portfolio"}
          >
            {packetBusy
              ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building packet…</>)
              : (<><FileDown className="w-4 h-4 mr-2" /> Weekly packet</>)}
          </Button>
          <Button
            variant="outline"
            onClick={clearVisibleWeek}
            disabled={clearBusy}
            className="rounded-full border-2 border-stone-200 hover:border-red-300 hover:text-red-600"
            data-testid="clear-week-btn"
            title="Clear all planned lessons in this week (preserves field trips, family days, sports, and holidays)"
          >
            <Eraser className="w-4 h-4 mr-2" /> {clearBusy ? "Clearing…" : "Clear week"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openAdd(ymd(new Date()))} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-lesson-btn">
                <Plus className="w-4 h-4 mr-2" /> Add to calendar
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg rounded-2xl" data-testid="lesson-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">{editingId ? "Edit entry" : "Add to calendar"}</DialogTitle>
                <DialogDescription>Plan a lesson for a student — or block time for a field trip, sports, family day, or holiday.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Entry type</Label>
                  <div className="mt-2 grid grid-cols-5 gap-1.5" data-testid="entry-type-picker">
                    {ENTRY_TYPES.map((t) => {
                      const TIcon = t.icon;
                      const active = form.entry_type === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setForm({ ...form, entry_type: t.id, student_id: t.id === "lesson" ? form.student_id : "" })}
                          className={`rounded-xl px-2 py-2.5 text-[11px] font-medium border-2 transition-all flex flex-col items-center gap-1 ${active ? `${t.ring} ${t.bg} ${t.textClass}` : "border-stone-200 bg-white text-stone-500 hover:border-stone-300"}`}
                          data-testid={`entry-type-${t.id}`}
                        >
                          <TIcon className={`w-4 h-4 ${active ? t.iconClass : ""}`} />
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {form.entry_type === "lesson" && (
                  <div>
                    <Label>Student</Label>
                    <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                      <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="lesson-student-select"><SelectValue placeholder="Choose student" /></SelectTrigger>
                      <SelectContent>
                        {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {form.entry_type !== "lesson" && (
                  <div>
                    <Label>For (optional)</Label>
                    <Select value={form.student_id || "__all__"} onValueChange={(v) => setForm({ ...form, student_id: v === "__all__" ? "" : v })}>
                      <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="event-student-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">Whole family</SelectItem>
                        {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 rounded-xl h-11" placeholder={form.entry_type === "lesson" ? "Fractions warm-up" : "MOSI Museum trip"} data-testid="lesson-title-input" />
                </div>
                {form.entry_type === "lesson" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Subject</Label>
                      <Select value={form.subject || "Math"} onValueChange={(v) => setForm({ ...form, subject: v })}>
                        <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="lesson-subject-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Duration (min)</Label>
                      <Input type="number" min={5} step={5} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} className="mt-2 rounded-xl h-11" data-testid="lesson-duration-input" />
                    </div>
                  </div>
                )}
                {form.entry_type !== "lesson" && (
                  <div>
                    <Label>Duration (min)</Label>
                    <Input type="number" min={5} step={5} value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} className="mt-2 rounded-xl h-11" data-testid="event-duration-input" />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date</Label>
                    <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="lesson-date-input" />
                  </div>
                  <div>
                    <Label>Time</Label>
                    <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="lesson-time-input" />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-xl min-h-[70px]" data-testid="lesson-notes-input" />
                </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {editingId && (
                  <Button
                    variant="outline"
                    onClick={async () => {
                      // Route through remove() so the Undo toast pattern applies.
                      const snapshot = { id: editingId, ...form };
                      setOpen(false);
                      setEditingId(null);
                      await remove(snapshot);
                    }}
                    className="rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 mr-auto"
                    data-testid="lesson-dialog-delete"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                )}
                <Button variant="ghost" onClick={() => { setOpen(false); setEditingId(null); }} className="rounded-full">Cancel</Button>
                <Button onClick={save} disabled={saving || !form.title || (form.entry_type === "lesson" && !form.student_id)} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="lesson-save-btn">
                  {saving ? "Saving…" : editingId ? "Update" : (form.entry_type === "lesson" ? "Add lesson" : `Add ${entryMeta(form.entry_type).label.toLowerCase()}`)}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {studentFilter === "all" && students.length > 1 ? (
        <div className="space-y-4" data-testid="side-by-side-mode">
          {students.map((stu) => {
            const studentLessons = lessons.filter((l) => l.student_id === stu.id);
            const byDate = {};
            for (const l of studentLessons) (byDate[l.date] = byDate[l.date] || []).push(l);
            return (
              <div key={stu.id} className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden" data-testid={`student-lane-${stu.id}`}>
                <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-3" style={{ borderLeft: `6px solid ${studentColor(stu.id)}` }}>
                  <span className="w-3 h-3 rounded-full" style={{ background: studentColor(stu.id) }} />
                  <div className="font-display font-semibold">{stu.name}</div>
                  <div className="text-xs text-stone-500">Grade {stu.grade} · {stu.learning_style}</div>
                  <div className="ml-auto text-xs text-stone-500">{studentLessons.filter(l => days.some(d => ymd(d) === l.date)).length} lesson(s) this week</div>
                </div>
                <div className="p-2 overflow-x-auto">
                  <div className="grid grid-cols-7 min-w-[900px] gap-2">
                    {days.map((d) => {
                      const key = ymd(d);
                      const items = byDate[key] || [];
                      const isToday = ymd(new Date()) === key;
                      return (
                        <div key={key} className={`min-h-[140px] rounded-lg p-2 ${isToday ? "bg-amber-50/60 ring-1 ring-amber-200" : "bg-stone-50"}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-[9px] uppercase tracking-[0.2em] font-semibold text-stone-500">{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                              <div className="font-display text-sm font-bold">{d.getDate()}</div>
                            </div>
                            <button onClick={() => { setForm({ ...emptyLesson, date: key, student_id: stu.id }); setOpen(true); }} className="w-5 h-5 rounded-full bg-white border border-stone-200 grid place-items-center text-stone-500 hover:text-amber-600" title="Add lesson">
                              <Plus className="w-2.5 h-2.5" />
                            </button>
                          </div>
                          <div className="mt-1.5 space-y-1.5">
                            {items.map((l) => {
                              const em = entryMeta(l.entry_type || "lesson");
                              const isLesson = (l.entry_type || "lesson") === "lesson";
                              const EmIcon = em.icon;
                              return (
                              <div key={l.id} className={`rounded p-1.5 text-[11px] ${em.bg} border-l-4 border ${l.status === "completed" ? "border-emerald-200 opacity-70" : em.ring} shadow-sm hover:shadow-md cursor-pointer transition-shadow`} style={isLesson ? { borderLeftColor: studentColor(stu.id) } : {}} onClick={() => isLesson ? openHelp(l) : openEdit(l)} data-testid={`lane-lesson-${l.id}`}>
                                <div className="flex items-center gap-1">
                                  {!isLesson && <EmIcon className={`w-3 h-3 shrink-0 ${em.iconClass}`} />}
                                  <div className="font-medium text-stone-900 truncate flex-1">{l.title}</div>
                                </div>
                                <div className="text-stone-500 text-[10px]">
                                  {isLesson ? `${l.subject} · ${l.time}` : `${em.label} · ${l.time}`}
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div className="bg-white border border-stone-200 rounded-2xl p-2 shadow-sm overflow-x-auto">
        <div className="grid grid-cols-7 min-w-[900px] gap-2">
          {days.map((d) => {
            const key = ymd(d);
            const items = lessonsByDate[key] || [];
            const isToday = ymd(new Date()) === key;
            return (
              <div key={key} className={`min-h-[220px] rounded-xl p-3 ${isToday ? "bg-amber-50/60 ring-1 ring-amber-200" : "bg-stone-50"}`} data-testid={`day-col-${key}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-stone-500">
                      {d.toLocaleDateString("en-US", { weekday: "short" })}
                    </div>
                    <div className="font-display text-lg font-bold">{d.getDate()}</div>
                  </div>
                  <button onClick={() => openAdd(key)} className="w-6 h-6 rounded-full bg-white border border-stone-200 grid place-items-center text-stone-500 hover:text-amber-600 hover:border-amber-300" data-testid={`add-on-${key}`}>
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
                <div className="mt-2 space-y-2">
                  {items.map((l) => {
                    const em = entryMeta(l.entry_type || "lesson");
                    const isLesson = (l.entry_type || "lesson") === "lesson";
                    const EmIcon = em.icon;
                    return (
                    <div key={l.id} className={`rounded-lg p-2.5 text-xs ${em.bg} border-l-4 border ${l.status === "completed" ? "border-emerald-200 opacity-70" : em.ring} shadow-sm hover:shadow-md cursor-pointer transition-shadow`} style={isLesson ? { borderLeftColor: studentColor(l.student_id) } : {}} onClick={() => isLesson ? openHelp(l) : openEdit(l)} data-testid={`lesson-card-${l.id}`}>
                      <div className="flex items-start justify-between gap-1">
                        <div className="min-w-0 flex-1 flex items-start gap-1">
                          {!isLesson && <EmIcon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${em.iconClass}`} />}
                          <div className="font-semibold text-stone-900 truncate">{l.title}</div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                          {isLesson && (
                            <button onClick={() => regenerateLesson(l)} disabled={regenBusy === l.id} className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 grid place-items-center disabled:opacity-50 ring-1 ring-amber-200" data-testid={`regen-lesson-${l.id}`} title="Suggest a new lesson">
                              {regenBusy === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          <button onClick={() => openEdit(l)} className="w-6 h-6 rounded-full bg-stone-100 text-stone-600 hover:bg-blue-100 hover:text-blue-700 grid place-items-center ring-1 ring-stone-200" data-testid={`edit-lesson-${l.id}`} title="Edit">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => toggleDone(l)} className={`w-6 h-6 rounded-full grid place-items-center ring-1 ${l.status === "completed" ? "bg-emerald-500 text-white ring-emerald-600" : "bg-emerald-50 text-emerald-600 ring-emerald-200 hover:bg-emerald-100"}`} data-testid={`toggle-lesson-${l.id}`} title="Toggle complete">
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                      <div className="text-stone-500 mt-1">
                        {isLesson ? `${l.subject} · ${l.time}` : `${em.label} · ${l.time}`}
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <div className="flex items-center gap-1 min-w-0">
                          {isLesson ? (
                            <>
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: studentColor(l.student_id) }} />
                              <span className="text-stone-500 truncate">{studentName(l.student_id)}</span>
                            </>
                          ) : (
                            <span className={`${em.textClass} truncate`}>{l.student_id ? studentName(l.student_id) : "Whole family"}</span>
                          )}
                        </div>
                        {isLesson && <span className="text-amber-600 flex items-center gap-0.5"><Sparkles className="w-3 h-3" /></span>}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      )}

      {/* Lesson help modal */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="lesson-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Info className="w-5 h-5 text-amber-600" /> {helpLesson?.title || "Lesson"}
            </DialogTitle>
            <DialogDescription>
              {helpLesson ? (
                <>
                  {helpLesson.subject} · {helpLesson.date} · {helpLesson.time} · {studentName(helpLesson.student_id)}
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          {helpLesson?.notes && (
            <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-sm text-stone-700">
              <span className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">Notes: </span>{helpLesson.notes}
            </div>
          )}

          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4" data-testid="lesson-help-content">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-amber-800">
                <Sparkles className="w-3.5 h-3.5" /> Teaching help (tailored to this student)
              </div>
              <div className="inline-flex items-center gap-1 rounded-full bg-white/70 border border-amber-200 p-1 text-xs" data-testid="learning-style-picker">
                <button
                  type="button"
                  onClick={() => regenerateHelpWithStyle("")}
                  disabled={helpBusy}
                  className={`px-2.5 py-1 rounded-full font-medium transition-colors ${helpStyle === "" ? "bg-amber-500 text-white shadow-sm" : "text-stone-600 hover:bg-amber-100"}`}
                  data-testid="style-default"
                  title="Use the student's profile default learning style"
                >
                  Default
                </button>
                <button
                  type="button"
                  onClick={() => regenerateHelpWithStyle("visual")}
                  disabled={helpBusy}
                  className={`px-2.5 py-1 rounded-full font-medium transition-colors inline-flex items-center gap-1 ${helpStyle === "visual" ? "bg-amber-500 text-white shadow-sm" : "text-stone-600 hover:bg-amber-100"}`}
                  data-testid="style-visual"
                >
                  <Eye className="w-3 h-3" /> Visual
                </button>
                <button
                  type="button"
                  onClick={() => regenerateHelpWithStyle("auditory")}
                  disabled={helpBusy}
                  className={`px-2.5 py-1 rounded-full font-medium transition-colors inline-flex items-center gap-1 ${helpStyle === "auditory" ? "bg-amber-500 text-white shadow-sm" : "text-stone-600 hover:bg-amber-100"}`}
                  data-testid="style-auditory"
                >
                  <Ear className="w-3 h-3" /> Auditory
                </button>
                <button
                  type="button"
                  onClick={() => regenerateHelpWithStyle("kinesthetic")}
                  disabled={helpBusy}
                  className={`px-2.5 py-1 rounded-full font-medium transition-colors inline-flex items-center gap-1 ${helpStyle === "kinesthetic" ? "bg-amber-500 text-white shadow-sm" : "text-stone-600 hover:bg-amber-100"}`}
                  data-testid="style-kinesthetic"
                >
                  <Hand className="w-3 h-3" /> Kinesthetic
                </button>
              </div>
            </div>
            {helpBusy ? (
              <div className="mt-3 flex items-center gap-2 text-sm text-stone-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Preparing suggestions{helpStyle ? ` in ${helpStyle} style` : ""}…
              </div>
            ) : helpContent ? (
              <div className="mt-3 prose prose-sm max-w-none text-stone-800 leading-relaxed">
                {helpContent.split("\n").map((line, i) => {
                  const html = line
                    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-stone-900">$1</strong>')
                    .replace(/`(.+?)`/g, '<code class="bg-stone-100 px-1 rounded">$1</code>');
                  return <p key={i} className="my-1" dangerouslySetInnerHTML={{ __html: html }} />;
                })}
              </div>
            ) : (
              <div className="mt-3 text-sm text-stone-500">No suggestions available.</div>
            )}
          </div>

          {/* Nano Banana illustrations panel removed — see /help/pdf for structured text-first output */}

          {/* Textbook + YouTube recommendations */}
          {helpLesson && (
            <div className="rounded-xl bg-emerald-50/50 border border-emerald-200 p-4" data-testid="lesson-resources-panel">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-emerald-800">
                  <BookOpen className="w-3.5 h-3.5" /> Textbooks &amp; Videos to support this lesson
                </div>
                {resources ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => loadResources(helpLesson.id, true)}
                    disabled={resourcesBusy}
                    className="rounded-full text-emerald-700 hover:bg-emerald-100"
                    data-testid="lesson-resources-refresh"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 mr-1 ${resourcesBusy ? "animate-spin" : ""}`} /> Regenerate
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => loadResources(helpLesson.id, false)}
                    disabled={resourcesBusy}
                    className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="lesson-resources-load"
                  >
                    {resourcesBusy ? (<><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Loading…</>) : (<><Sparkles className="w-3.5 h-3.5 mr-1" /> Get suggestions</>)}
                  </Button>
                )}
              </div>

              {resourcesBusy && !resources && (
                <div className="mt-3 text-sm text-stone-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Picking books and video queries…
                </div>
              )}

              {resources && (
                <div className="mt-4 grid md:grid-cols-2 gap-4">
                  <div data-testid="lesson-resources-textbooks">
                    <div className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500 flex items-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5" /> Textbooks
                    </div>
                    {resources.textbooks?.length ? (
                      <ul className="mt-2 space-y-2">
                        {resources.textbooks.map((b, i) => (
                          <li key={i} className="rounded-lg bg-white border border-stone-200 p-2.5" data-testid={`textbook-${i}`}>
                            <div className="text-sm font-semibold text-stone-900">{b.title}</div>
                            {b.author && <div className="text-xs text-stone-500">{b.author}</div>}
                            {b.why && <div className="mt-1 text-xs text-stone-600 leading-snug">{b.why}</div>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-2 text-xs text-stone-500">No textbook picks yet.</div>
                    )}
                  </div>
                  <div data-testid="lesson-resources-videos">
                    <div className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500 flex items-center gap-1.5">
                      <Youtube className="w-3.5 h-3.5 text-red-600" /> YouTube videos
                    </div>
                    {resources.videos?.length ? (
                      <ul className="mt-2 space-y-2">
                        {resources.videos.map((v, i) => (
                          <li key={i} className="rounded-lg bg-white border border-stone-200 p-2.5" data-testid={`video-${i}`}>
                            <a
                              href={v.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm font-semibold text-red-700 hover:text-red-800 hover:underline inline-flex items-center gap-1"
                              data-testid={`video-link-${i}`}
                            >
                              <Youtube className="w-3.5 h-3.5" /> {v.title}
                            </a>
                            {v.channel && <div className="text-xs text-stone-500">{v.channel}</div>}
                            {v.why && <div className="mt-1 text-xs text-stone-600 leading-snug">{v.why}</div>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-2 text-xs text-stone-500">No video picks yet.</div>
                    )}
                  </div>
                </div>
              )}

              {resources?.note && (
                <div className="mt-3 text-xs text-emerald-900 bg-emerald-100/60 border border-emerald-200 rounded-lg p-2 leading-snug" data-testid="lesson-resources-note">
                  <strong>Tip:</strong> {resources.note}
                </div>
              )}
            </div>
          )}

          {/* Worksheet generator (Premium) */}
          {helpLesson && (
            <WorksheetPanel lesson={helpLesson} isPremium={isPremium(helpLesson.student_id)} />
          )}

          <DialogFooter className="flex-wrap gap-2">
            <Button variant="ghost" onClick={() => setHelpOpen(false)} className="rounded-full">Close</Button>
            {helpLesson && (
              <>
                <Button
                  variant="outline"
                  onClick={exportHelpPdf}
                  disabled={pdfBusy || helpBusy || !helpContent}
                  className="rounded-full border-2 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50"
                  data-testid="lesson-help-export-pdf"
                >
                  {pdfBusy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</>) : (<><FileDown className="w-4 h-4 mr-2" /> Export as PDF</>)}
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => { const l = helpLesson; setHelpOpen(false); await remove(l); }}
                  className="rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300"
                  data-testid="lesson-help-delete"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { const l = helpLesson; setHelpOpen(false); openEdit(l); }}
                  className="rounded-full border-2 border-stone-200"
                  data-testid="lesson-help-edit"
                >
                  <Pencil className="w-4 h-4 mr-2" /> Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => { await regenerateLesson(helpLesson); setHelpOpen(false); }}
                  disabled={regenBusy === helpLesson.id}
                  className="rounded-full border-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="lesson-help-regenerate"
                >
                  {regenBusy === helpLesson.id ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Rethinking…</>) : (<><Wand className="w-4 h-4 mr-2" /> Suggest new lesson</>)}
                </Button>
                <Button
                  onClick={async () => { await toggleDone(helpLesson); setHelpOpen(false); }}
                  className={`rounded-full ${helpLesson.status === "completed" ? "bg-stone-900 hover:bg-stone-800" : "bg-emerald-600 hover:bg-emerald-700"} text-white`}
                  data-testid="lesson-help-toggle"
                >
                  <Check className="w-4 h-4 mr-2" /> {helpLesson.status === "completed" ? "Mark planned" : "Mark completed"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto-build dialog */}
      <Dialog open={autoOpen} onOpenChange={setAutoOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="auto-build-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-emerald-600" /> Auto-build a week
            </DialogTitle>
            <DialogDescription>
              We'll weight subjects by weakness (from the student's profile) and generate lessons across the week.
              You can still edit each lesson afterwards. Nothing is created until you click Build.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Student</Label>
              <Select value={autoForm.student_id} onValueChange={(v) => setAutoForm({ ...autoForm, student_id: v })}>
                <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="auto-student-select"><SelectValue placeholder="Choose student" /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — G{s.grade}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start date</Label>
                <Input type="date" value={autoForm.start_date} onChange={(e) => setAutoForm({ ...autoForm, start_date: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="auto-start-date" />
              </div>
              <div>
                <Label>Days (Mon–Fri only)</Label>
                <Input type="number" min={1} max={10} value={autoForm.days} onChange={(e) => setAutoForm({ ...autoForm, days: parseInt(e.target.value) || 5 })} className="mt-2 rounded-xl h-11" data-testid="auto-days" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Lessons per day</Label>
                <Input type="number" min={1} max={6} value={autoForm.lessons_per_day} onChange={(e) => setAutoForm({ ...autoForm, lessons_per_day: parseInt(e.target.value) || 4 })} className="mt-2 rounded-xl h-11" data-testid="auto-lpd" />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" min={15} max={120} step={5} value={autoForm.duration_minutes} onChange={(e) => setAutoForm({ ...autoForm, duration_minutes: parseInt(e.target.value) || 45 })} className="mt-2 rounded-xl h-11" data-testid="auto-duration" />
              </div>
            </div>
            <div className="rounded-xl border border-stone-200 p-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Personalized titles</div>
                  <div className="text-xs text-stone-500 mt-1">Uses the student's learning style + interests.</div>
                </div>
                <Switch checked={autoForm.use_ai} onCheckedChange={(v) => setAutoForm({ ...autoForm, use_ai: v })} data-testid="auto-use-ai" />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="font-medium">Replace existing planned lessons in the window</div>
                  <div className="text-xs text-stone-500 mt-1">Off keeps your current lessons; on wipes them for a clean rebuild.</div>
                </div>
                <Switch checked={autoForm.replace_existing} onCheckedChange={(v) => setAutoForm({ ...autoForm, replace_existing: v })} data-testid="auto-replace" />
              </div>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
              💡 Set the student's proficiencies on their <span className="font-semibold">Profile</span> page first — subjects with lower scores get more slots.
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setAutoOpen(false)} className="rounded-full">Cancel</Button>
            <Button
              onClick={runAutoBuild}
              disabled={autoBusy || !autoForm.student_id}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="auto-build-confirm"
            >
              {autoBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</> : <><Wand2 className="w-4 h-4 mr-2" /> Build week</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
